import React, {
  useState,
  FunctionComponent,
  useContext,
  useEffect,
} from "react";
import {
  styled,
  Block,
  Modal,
  PanelHeader,
  IModalProps,
  Checkbox,
  Icon,
  FullInput,
} from "../../../seenspire-library";
import PanelForm from "../../components/PanelForm/PanelForm";
import { Formik, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Invite, Invite2, Role } from "seenspire-api-client";
import {
  formatServerValidationErrors,
  getCurrentRole,
  getCurrentRoles,
  isClient,
} from "../../util";
import InputField from "../../components/InputField/InputField";
import { AppContext } from "../../state";
import { useTranslation } from "react-i18next";
import {
  ModalButtons,
  FormModalButtons,
} from "../../components/ModalButtons/ModalButtons";
import { find, isEmpty } from "lodash";
import { AccountUserRoleMe, UserRolesEnum } from "../../interfaces";

export interface UserRole extends Role {
  selected: boolean;
}

const Title = styled(Block)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-top: 10px;
  text-transform: uppercase;
`;

const EditUserModal: FunctionComponent<IModalProps & { user: any }> = ({
  onSubmit,
  onCancel,
  user: originalUser,
  ...props
}) => {
  const { slug, client, account, theme } = useContext(AppContext);
  const { t } = useTranslation();
  const [user, setUser] = useState<any>({ email: "", role: [] });
  const [roles, setRoles] = useState<UserRole[]>();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const _userRoles = await client.Users.getMe().then(
      (d) => getCurrentRoles(originalUser, account) as Role[]
    );
    const _roles = await client.Users.getRoles().then(
      (d: any) =>
        d.data.filter(
          (role: any) =>
            role.type === "General" ||
            (!isClient(account.account) && role.type === "Partner")
        ) as Role[]
    );
    const selectedUserRoles: UserRole[] = _roles.map((role) => {
      return {
        ...role,
        selected: !!_userRoles.find(
          (userRole) => userRole.id === role.id || userRole.name === role.name
        ),
      };
    });
    setRoles(selectedUserRoles);
  };

  const submit = async (values: any, actions: FormikHelpers<any>) => {
    if (!roles || isEmpty(roles)) return;

    const role = roles.filter((r) => r.selected).map((r) => ({ id: r.id }));

    try {
      await client.Users.putAccountUser(originalUser.id, {
        id: originalUser.id,
        account: account.account.id,
        role,
      } as any);
      onSubmit();
    } catch (error) {
      actions.setErrors(formatServerValidationErrors(error));
      actions.setSubmitting(false);
    }
  };

  const selectRole = (role: Role) => {
    if (!roles) return;
    const _roles = roles.map((r) => {
      if (r.id === role.id) {
        r.selected = true;
      } else if (r.slug !== UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER) {
        r.selected = false;
      }
      return r;
    });
    setRoles(_roles);
  };

  if (!roles) return null;
  return (
    <Modal onRequestClose={() => onCancel()} theme={theme} {...props}>
      <Formik initialValues={user} onSubmit={submit} enableReinitialize>
        {(form) => (
          <>
            <PanelForm form={form} hideHeader disable={!user}>
              <PanelHeader
                title={t("accounts.user_role_title")}
                description={t("accounts.user_role_description")}
              />

              <FullInput
                title="Name"
                disabled={true}
                value={`${originalUser.first_name || ""} ${
                  originalUser.last_name || ""
                }`}
              />

              <Title padding={{ top: 12 }}>Account roles</Title>
              <Block display="grid" gridGap={5}>
                {roles
                  .filter(
                    (role) =>
                      role.slug !== UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                  )
                  .map((role) => (
                    <Block
                      display="grid"
                      justifySelf="start"
                      direction="column"
                      gridGap={10}
                      cursor="pointer"
                      onClick={() => selectRole(role)}
                    >
                      <Icon
                        margin={{ left: -2 }}
                        justifySelf="start"
                        color={role.selected ? "cta" : "gray"}
                        name={role.selected ? "radioOn" : "radioOff"}
                      />
                      <Block
                        display="grid"
                        justifySelf="start"
                        alignItems="center"
                        color="gray"
                        shade="darkest"
                      >
                        {role.name}{" "}
                      </Block>
                    </Block>
                  ))}
              </Block>

              {!isClient(account.account) && (
                <>
                  <Title>{t("accounts.partner_portal_access_label")}</Title>
                  <Checkbox
                    title="Partner Portal Manager"
                    fontWeight="regular"
                    checked={
                      !!find(
                        roles,
                        (r) =>
                          r.selected &&
                          r.slug === UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                      )
                    }
                    onChange={() => {
                      setRoles([
                        ...roles.map((role) => {
                          if (
                            role.slug ===
                            UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                          ) {
                            role.selected = !role.selected;
                          }
                          return role;
                        }),
                      ]);
                    }}
                  />
                </>
              )}
            </PanelForm>

            <FormModalButtons
              outsideForm
              form={form}
              onCancel={onCancel}
              submit={t("Submit")}
              cancel={t("Cancel")}
            />
          </>
        )}
      </Formik>
    </Modal>
  );
};

export { EditUserModal };
